import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader } from "../../components/bits";

const cellCls = (ratio) => {
  if (ratio > 1) return "bg-red-500/85 text-white font-bold";
  if (ratio > 0.8) return "bg-amber-400/80 text-neutral-900 font-semibold";
  if (ratio > 0) return "bg-emerald-200 text-neutral-900";
  return "text-muted-foreground";
};

export default function CapacityPlanPage() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/capacity`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);

  if (!data) return <div className="text-muted-foreground">Loading capacity…</div>;

  return (
    <div data-testid="capacity-plan-page">
      <PageHeader overline="Plan · Next 8 weeks" title="Resource Capacity & Demand">
        <div className="text-xs text-muted-foreground mt-1">
          Allocated hours = assigned open task effort (duration × 8h ÷ assignees) spread across each task's window. Set weekly capacity on Resource Workload. Logged hours from Timesheet shown as <em>actual</em>.
        </div>
      </PageHeader>
      <div className="border border-border bg-card overflow-auto mt-3">
        <table className="w-full data-table text-sm">
          <thead>
            <tr>
              <th className="whitespace-nowrap">Member</th>
              <th className="whitespace-nowrap">Capacity (h/wk)</th>
              {data.weeks.map((w) => <th key={w} className="text-center whitespace-nowrap">wk {w.slice(5)}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.members.length === 0 && <tr><td colSpan={10} className="text-center text-muted-foreground py-8">No members — add people on Members & Roles.</td></tr>}
            {data.members.map((m) => (
              <tr key={m.userId} data-testid="capacity-row">
                <td className="font-medium whitespace-nowrap">{m.name}<div className="text-[10px] text-muted-foreground uppercase">{m.role}</div></td>
                <td className="text-center">{m.capacity}h</td>
                {m.allocated.map((a, i) => {
                  const ratio = m.capacity > 0 ? a / m.capacity : 0;
                  return (
                    <td key={i} className="p-0">
                      <div data-testid={`capacity-cell-${m.userId}-${i}`} className={`px-2 py-2.5 text-center text-xs ${cellCls(ratio)}`}
                        title={`Allocated ${a}h of ${m.capacity}h · logged ${m.actual[i]}h`}>
                        {a > 0 ? `${a}h` : "—"}
                        {m.actual[i] > 0 && <div className="text-[9px] opacity-80">✓ {m.actual[i]}h</div>}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-200 inline-block" /> under 80%</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-400/80 inline-block" /> 80–100%</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-red-500/85 inline-block" /> over capacity</span>
      </div>
    </div>
  );
}
